import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import config from '../../config';
import { signOutUser } from '../../slices/AuthSlice';
import { useAppDispatch } from '../../slices/store';
import { getAuthCookie } from '../../utils/AuthUtils';

interface AgentRouteProps {
  children: React.ReactNode;
  redirectType?: 'login' | 'signup';
  authenticated?: boolean;
}

const AgentRoute: React.FC<AgentRouteProps> = ({
  children,
  redirectType = 'login',
  authenticated = false,
}) => {
  const dispatch = useAppDispatch();
  const isRBOEnabled = config.isRBOEnabled;
  const boltBaseUrl = config.boltBaseUrl;
  const isExternalRedirect = !isRBOEnabled && !!boltBaseUrl;

  const redirectPath = redirectType === 'login' ? '/login' : '/register';
  const redirectUrl = isExternalRedirect
    ? `${boltBaseUrl}${redirectPath}`
    : redirectType === 'login'
    ? '/login'
    : '/signup';

  // For authenticated routes, check if user has token and logout if needed
  useEffect(() => {
    if (isExternalRedirect && authenticated) {
      const hasToken = !!getAuthCookie();

      if (hasToken) {
        // User has valid token - logout server session, clear cookies, reset state
        dispatch(signOutUser()).then(() => {
          // Redirect to Bolt login page (user needs to login again)
          window.location.href = `${boltBaseUrl}/login`;
        });
      } else {
        // No token - just redirect to Bolt login (no logout needed)
        window.location.href = `${boltBaseUrl}/login`;
      }
    }
  }, [isExternalRedirect, authenticated, dispatch, boltBaseUrl]);

  // Handle external redirect via useEffect for guest users
  useEffect(() => {
    if (isExternalRedirect && !authenticated) {
      window.location.href = redirectUrl;
    }
  }, [isExternalRedirect, authenticated, redirectUrl]);

  // Early return if RBO is enabled - most performant path
  if (isRBOEnabled) {
    return <>{children}</>;
  }

  // Return null while redirecting externally to prevent flash
  if (isExternalRedirect) {
    return null;
  }

  // Internal redirect fallback
  return <Navigate to={redirectUrl} replace />;
};

export default AgentRoute;
